import { destination,distance } from "@turf/turf";

const innerRate = 1.0;
const outerRate = 1.33;
const defaultOpacity = 0.75;
const rateOutOfArea = 0.00001; // エリア外の菌の食事量減衰率
const maxRecognizeR = 100; // 認知による拡大の最大半径
const minR = 5;
const maxR = 20;

class Bacteria {
  #baseLat; // 基準点の緯度
  #baseLng; // 基準点の経度
  #baseR;
  #phase; // 初期位相
  #frequency; // 呼吸の周波数
  healtPoints = 0; // 体力（保有栄養量）
  #radiusHistory = []; // 半径の履歴を保存  

  /**
   * コンストラクタ
   * @param {number} lat - 基準点の緯度
   * @param {number} lng - 基準点の経度
   * @param {number} initialNutrient - 初期栄養量
   * @param {string} route_color - この路線の色
   */
  constructor(lat, lng, initialNutrient, route_color,metabolizingNutrient,maxHealthPoint) {
    this.healtPoints += initialNutrient; // 初期付与量を追加
    const initialR = minR + Math.random()*(maxR-minR);
    this.R = initialR;
    this.#baseR = initialR;
    this.maxCapaciyOfEating = 0.001 + Math.random() * 0.002; // 栄養吸収の制限 

    this.lat = lat; // 基準点の緯度
    this.lng = lng; // 基準点の経度
    this.metabolizingNutrient = metabolizingNutrient;
    this.maxHealthPoint = maxHealthPoint; // 最大サイズ
    

    // 呼吸の初期位相と周波数をランダムに設定
    this.#phase = Math.random() * 2 * Math.PI; // ランダムな位相 (0 ~ 2π)
    this.#frequency = 0.0008 + Math.random() * 0.0004; // 周波数 (0.0008 ~ 0.0012)
    // this.#frequency = 1 / this.nucleusR; // 核の大きさの逆数を呼吸の周波数とする

    this.canSplit = false; // 吸い切った瞬間のみ分裂可能

    this.opacity = defaultOpacity;
    this.route_color = route_color; // 路線カラー

    // 栄養投下の認知係数
    this.recognizeRate = 1;
  }


  /* GETTER */

  /* SETTER */
  /** 栄養の追加 */
  addNutrient(nutrient){
    const updateHealthPoints = this.healtPoints + nutrient;
    this.healtPoints = Math.min(updateHealthPoints,this.maxHealthPoint);
    const updatedNucleusR = this.nucleusR + nutrient;
    this.nucleusR = Math.min(updatedNucleusR,this.maxHealthPoint);
  }

  /**
   * 菌の更新
   * @param {*} map 
   * @param {*} currentTimeStamp 
   */
  live(currentTimeStamp){
    this.breath(currentTimeStamp);
  }

  /**
   * 呼吸（細胞膜の周期的なサイズ変更）
   * @param {number} currentTimeStamp - 現在の時間（ミリ秒）
   */
  breath(currentTimeStamp) {
    const sizeFactor = Math.sin(currentTimeStamp * this.#frequency + this.#phase); // サインカーブの値
    const scaleFactor = 0.3; // 増減率 (±30%)
    this.R = this.#baseR * (1 + scaleFactor * sizeFactor);
    // 履歴に現在の半径を追加
    this.#radiusHistory.push(this.R);
    // 最大3つまでの履歴を保持
    if (this.#radiusHistory.length > 3) {
      this.#radiusHistory.shift();
    }
    // 増加率の変化を検出
    if (this.#radiusHistory.length === 3) {
      const [r1, r2, r3] = this.#radiusHistory; // 直近3つの値
      const rate1 = r2 - r1; // 増加率1
      const rate2 = r3 - r2; // 増加率2
      // 前回増加率が正、今回増加率が負になった場合
      if (rate1 > 0 && rate2 < 0 && !this.canSplit) {
        this.canSplit = true; // フラグを立てる
      } else if (rate2 >= 0) {
        this.canSplit = false; // フラグをリセット
      }
    }
  }

  /**
   * 消費（healthPointsの消費）
   * @param {*} sec 何秒間か？
   */
  metabolize(sec=1){
    const updated = this.healtPoints - this.metabolizingNutrient * sec;
    if (updated > 0) {
      this.healtPoints = updated;
    } else {
      this.healtPoints = 0;
    }
  }

  /**
   * 衰弱
   * hpが少なくなると薄くなっていく
   */
  weaken(){
    const updatedOpacity = Math.min(this.healtPoints/this.maxHealthPoint,1);
    this.opacity = 0.2 + updatedOpacity*0.8; // 0.25 - 1.0の間に補正
  }
  /**
   * ctx向けに色を作成する．
   * @returns 
   */
  rgba(){
    // 6文字のHEX値を分解してRGBに変換
    const r = parseInt(this.route_color.slice(0, 2), 16);
    const g = parseInt(this.route_color.slice(2, 4), 16);
    const b = parseInt(this.route_color.slice(4, 6), 16);
    // RGBA形式の文字列を返す
    return `rgba(${r}, ${g}, ${b}, ${defaultOpacity})`;
  }


  /**
   * Bacteriaの細胞分裂
   * @param {Bacteria[]} bacteria 対象のバクテリア
   * @param {Stop} ownerStop オーナー
   */
  static split(bacteria,ownerStop) {
    const initialNutrient = bacteria.healtPoints / 3; // その時点での体力をに分割する
    const angle1 = Math.random() * 180;
    const angle2 = angle1 + 180;
    const distanceOfBuffer = bacteria.nucleusR*2;
    // 菌を生成する
    for (let angle of Array.of(angle1, angle2)){
      const destinationPoint = destination([bacteria.lng,bacteria.lat],distanceOfBuffer,angle,{units:"meters"});
      const [lng,lat] =destinationPoint.geometry.coordinates;
      const newedBacteria = new Bacteria(lat,lng,initialNutrient,bacteria.route_color);
      ownerStop.bacterias.add(newedBacteria);

      const distanceFromStop = distance([ownerStop.lng, ownerStop.lat], [newedBacteria.lng, newedBacteria.lat],{ units: "meters" })
      if (distanceFromStop > ownerStop.stationCoverageRadius){
        newedBacteria.maxCapaciyOfEating = newedBacteria.maxCapaciyOfEating * rateOutOfArea; // Stopから遠い位置に生まれると，長期的に死滅する．
      }
    }
    // 分裂元の菌を殺す
    ownerStop.bacterias.delete(bacteria);
    bacteria = null;
  }
}

export default Bacteria;
